/**
 * Prediction BI Service — wraps /api/admin/ai/predictions/ + /api/admin/ai/artifacts/
 * Phase 4.8-B — WQ-198 (initial: list/summary/export)
 * Phase 4.10 β3 — WQ-238: PredictionRequestForm helpers
 *
 * Named predictionBIService to avoid collision with existing prediction services.
 */

// === Types ===

export interface Prediction {
  id: string;
  model_id: string;
  model_name: string | null;
  input_data: Record<string, unknown>;
  output: Record<string, unknown> | null;
  prediction_type: string;
  batch_id: string | null;
  confidence: number | null;
  status: string;
  latency_ms: number | null;
  created_at: string;
}

export interface BatchSummary {
  batch_id: string;
  total: number;
  completed: number;
  failed: number;
  avg_confidence: number | null;
  avg_latency_ms: number | null;
  created_at: string;
}

export interface ModelStats {
  model_id: string;
  total_predictions: number;
  avg_confidence: number | null;
  avg_latency_ms: number | null;
  error_rate: number;
}

export interface PredictRequest {
  model_id: string;
  input_data: Record<string, unknown>;
  prediction_type?: string;
}

/**
 * Synchronous /predict response. Matches the backend's `PredictResponse`
 * pydantic model exactly.
 */
export interface PredictResponse {
  id: string;
  model_id: string;
  deployment_id: string;
  input_hash: string;
  output_value: Record<string, unknown>;
  confidence_score: number | null;
  prediction_type: string;
  created_at: string | null;
}

/**
 * Async /predict/batch request. Backend accepts a single catalog_entry_id
 * (uuid) plus an optional row limit; predictions are then materialized
 * server-side and accumulated under a generated batch_id.
 */
export interface BatchPredictRequest {
  model_id: string;
  catalog_entry_id: string;
  limit?: number;
}

export interface BatchPredictResponse {
  batch_id: string;
  predictions_created: number;
  mean_confidence: number | null;
  catalog_entry_id: string;
  model_id: string;
}

export interface ModelOption {
  id: string;
  name: string;
  problem_type?: string | null;
  algorithm_name?: string | null;
  deployment_id?: string | null;
  artifact_id?: string | null;
}

export interface ModelSchemaResponse {
  input_columns: Array<{ name: string; dtype: string; required?: boolean }>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function predict(
  authFetch: FetchFn,
  request: PredictRequest
): Promise<PredictResponse> {
  const res = await authFetch('/api/admin/ai/predictions/predict', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to predict: ${res.status}`);
  return res.json();
}

export async function predictBatch(
  authFetch: FetchFn,
  request: BatchPredictRequest
): Promise<BatchPredictResponse> {
  const res = await authFetch('/api/admin/ai/predictions/predict/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to predict batch: ${res.status}`);
  return res.json();
}

export async function listPredictions(
  authFetch: FetchFn,
  filters?: { model_id?: string; batch_id?: string; prediction_type?: string; limit?: number; offset?: number }
): Promise<Prediction[]> {
  const params = new URLSearchParams();
  if (filters?.model_id) params.set('model_id', filters.model_id);
  if (filters?.batch_id) params.set('batch_id', filters.batch_id);
  if (filters?.prediction_type) params.set('prediction_type', filters.prediction_type);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/predictions/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list predictions: ${res.status}`);
  return res.json();
}

export async function getPrediction(authFetch: FetchFn, id: string): Promise<Prediction> {
  const res = await authFetch(`/api/admin/ai/predictions/${id}`);
  if (!res.ok) throw new Error(`Failed to get prediction: ${res.status}`);
  return res.json();
}

export async function getBatchSummary(authFetch: FetchFn, batchId: string): Promise<BatchSummary> {
  const res = await authFetch(`/api/admin/ai/predictions/batch/${batchId}/summary`);
  if (!res.ok) throw new Error(`Failed to get batch summary: ${res.status}`);
  return res.json();
}

export async function exportBatchCsv(authFetch: FetchFn, batchId: string): Promise<Blob> {
  const res = await authFetch(`/api/admin/ai/predictions/batch/${batchId}/export`);
  if (!res.ok) throw new Error(`Failed to export batch: ${res.status}`);
  return res.blob();
}

export async function getModelStats(authFetch: FetchFn, modelId: string): Promise<ModelStats> {
  const res = await authFetch(`/api/admin/ai/predictions/models/${modelId}/stats`);
  if (!res.ok) throw new Error(`Failed to get model stats: ${res.status}`);
  return res.json();
}

export async function deletePrediction(authFetch: FetchFn, id: string): Promise<void> {
  const res = await authFetch(`/api/admin/ai/predictions/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete prediction: ${res.status}`);
}

// === WQ-238 helpers (Phase 4.10 β3 — PredictionRequestForm) ===

/**
 * List models that have an active champion deployment available for inference.
 * Hits /api/admin/ai/artifacts/deployments?role=champion and dedupes by model_id.
 *
 * Returns ModelOption[] suitable for the wizard's model picker.
 */
export async function listDeployedModels(authFetch: FetchFn): Promise<ModelOption[]> {
  const res = await authFetch('/api/admin/ai/artifacts/deployments?role=champion');
  if (!res.ok) throw new Error(`Failed to list deployed models: ${res.status}`);
  const deployments = (await res.json()) as Array<{
    id: string;
    artifact_id: string;
    model_id: string | null;
    deployment_role: string;
    is_active: boolean;
  }>;

  const seen = new Set<string>();
  const options: ModelOption[] = [];
  for (const d of deployments) {
    if (!d.is_active) continue;
    if (!d.model_id) continue;
    if (seen.has(d.model_id)) continue;
    seen.add(d.model_id);
    options.push({
      id: d.model_id,
      name: `Model ${d.model_id.slice(0, 8)} (artifact ${d.artifact_id.slice(0, 8)})`,
      deployment_id: d.id,
      artifact_id: d.artifact_id,
    });
  }
  return options;
}

/**
 * Best-effort model input-schema fetch. The backend may not expose a schema
 * endpoint; if the call fails, the caller should fall back to JSON-only input
 * mode (per WQ-238 guidance §EDGE CASES "Backend getModelSchema endpoint may
 * not exist").
 */
export async function getModelSchema(
  authFetch: FetchFn,
  modelId: string
): Promise<ModelSchemaResponse> {
  const res = await authFetch(`/api/admin/ai/models/${modelId}/schema`);
  if (!res.ok) throw new Error(`getModelSchema unavailable: ${res.status}`);
  return res.json();
}
